import { useState, useEffect, useCallback, useRef } from "react";
import { Maximize2, Camera, SlidersHorizontal, Timer, TrendingUp, TrendingDown, Menu } from "lucide-react";
import api from "../lib/api";
import { useMode } from "../context/ModeContext";
import PriceChart from "../components/PriceChart";
import MarketWatch from "../components/MarketWatch";
import TradePanel from "../components/TradePanel";
import TerminalHeadlines from "../components/TerminalHeadlines";
import { fmtPrice, pct, usd, signed, clsx } from "../lib/format";
import { INDICATOR_META } from "../lib/indicators";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "../components/ui/dropdown-menu";
import { Button } from "../components/ui/button";

const CHART_TYPES = [["candlestick", "Candles"], ["heikin", "Heikin Ashi"], ["line", "Line"], ["area", "Area"], ["bar", "Bar"]];
const TIMEFRAMES = ["5s", "10s", "15s", "30s", "1m", "2m", "5m", "10m", "15m", "30m", "1h", "4h", "1d"];
const OVERLAYS = ["MA", "EMA", "BB", "VWAP", "SAR", "ICHIMOKU"];
const OSCILLATORS = ["RSI", "MACD", "STOCH", "CCI", "ADX", "ATR", "VOL"];

export default function Terminal() {
  const { mode } = useMode();
  const [assets, setAssets] = useState([]);
  const [prices, setPrices] = useState({});
  const [symbol, setSymbol] = useState("BTC/USDT");
  const [timeframe, setTimeframe] = useState("1m");
  const [chartType, setChartType] = useState("candlestick");
  const [overlays, setOverlays] = useState(["MA"]);
  const [oscillator, setOscillator] = useState("RSI");
  const [candles, setCandles] = useState([]);
  const [openTrades, setOpenTrades] = useState([]);
  const [showMobileMarkets, setShowMobileMarkets] = useState(false);
  const [deskSidebar, setDeskSidebar] = useState(false);
  const chartWrapRef = useRef(null);

  const asset = assets.find((a) => a.symbol === symbol);
  const livePrice = prices[symbol] ?? asset?.price;

  useEffect(() => { api.get("/market/assets").then((r) => setAssets(r.data)); }, []);

  const loadCandles = useCallback(async () => {
    const { data } = await api.get("/market/candles", { params: { symbol, timeframe, count: 200 } });
    setCandles(data.candles);
  }, [symbol, timeframe]);

  useEffect(() => { loadCandles(); }, [loadCandles]);

  const loadOpen = useCallback(async () => {
    try { const { data } = await api.get("/trades/open"); setOpenTrades(data); } catch {}
  }, []);

  useEffect(() => {
    const t = setInterval(async () => {
      try { const { data } = await api.get("/market/prices"); setPrices(data); } catch {}
      loadCandles();
      loadOpen();
    }, 2000);
    loadOpen();
    return () => clearInterval(t);
  }, [loadCandles, loadOpen]);

  // local countdown tick
  useEffect(() => {
    const t = setInterval(() => setOpenTrades((prev) => prev.map((o) => ({ ...o, remaining: Math.max(0, o.remaining - 1) }))), 1000);
    return () => clearInterval(t);
  }, []);

  const toggleOverlay = (k) => setOverlays((o) => (o.includes(k) ? o.filter((x) => x !== k) : [...o, k]));

  const goFullscreen = () => { if (chartWrapRef.current?.requestFullscreen) chartWrapRef.current.requestFullscreen(); };
  const screenshot = () => {
    const c = chartWrapRef.current?.querySelector("canvas");
    if (c) { const a = document.createElement("a"); a.download = `${symbol.replace("/", "-")}.png`; a.href = c.toDataURL(); a.click(); }
  };

  return (
    <div className={clsx("flex flex-col lg:h-[calc(100vh-4rem)] lg:grid lg:grid-rows-1", deskSidebar ? "lg:grid-cols-[260px_1fr_300px]" : "lg:grid-cols-[1fr_300px]")}>
      {/* Mobile account / asset header (hidden on desktop) */}
      <div className="lg:hidden flex items-center justify-between gap-2 px-4 py-2.5 bg-fx-s1 border-b border-white/5" data-testid="mobile-terminal-header">
        <div className="flex items-center gap-2">
          <span className={clsx("px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase", mode === "live" ? "bg-gold/20 text-gold" : "bg-brandblue/20 text-brandblue")}>{mode}</span>
          <div>
            <div className="text-sm font-bold leading-none">{symbol}</div>
            <div className="text-xs font-mono text-fx-s3 mt-0.5">{livePrice ? fmtPrice(livePrice) : "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowMobileMarkets(true)} data-testid="mobile-asset-btn" className="px-3 py-1.5 rounded-lg bg-fx-base border border-white/10 text-xs font-medium flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> Assets</button>
          <a href="/wallet" data-testid="mobile-wallet-link" className="px-3 py-1.5 rounded-lg bg-brandblue text-white text-xs font-medium">Wallet</a>
        </div>
      </div>

      {/* Market watch */}
      <aside className={clsx("border-r border-white/5 bg-fx-s1/40", deskSidebar ? "lg:block" : "lg:hidden", showMobileMarkets ? "fixed inset-0 z-40 bg-fx-base pt-16" : "hidden")}>
        <MarketWatch assets={assets} prices={prices} selected={symbol} onSelect={(s) => { setSymbol(s); setShowMobileMarkets(false); }} />
      </aside>

      {/* Center */}
      <section className="flex flex-col min-w-0 overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5 flex-wrap">
          <button className="lg:hidden text-fx-s3" onClick={() => setShowMobileMarkets(true)} data-testid="mobile-markets-btn"><Menu className="h-5 w-5" /></button>
          <button className="hidden lg:inline-flex items-center gap-1 text-fx-s3 hover:text-white text-xs pr-2" onClick={() => setDeskSidebar((v) => !v)} data-testid="sidebar-toggle-btn"><Menu className="h-5 w-5" /> {deskSidebar ? "Hide" : "Markets"}</button>
          <div className="flex items-center gap-2 pr-3 border-r border-white/10">
            <span className="font-display font-bold text-white" data-testid="terminal-symbol">{symbol}</span>
            <span className="font-mono text-sm text-white" data-testid="terminal-price">{fmtPrice(livePrice)}</span>
            <span className={clsx("font-mono text-xs", (asset?.change_24h ?? 0) >= 0 ? "text-pos" : "text-neg")}>{pct(asset?.change_24h ?? 0)}</span>
          </div>

          <Select value={chartType} onValueChange={setChartType}>
            <SelectTrigger data-testid="charttype-select" className="h-8 w-[130px] bg-fx-s1 border-white/10 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-fx-s2 border-white/10 text-white">
              {CHART_TYPES.map(([v, l]) => <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1 overflow-x-auto">
            {TIMEFRAMES.map((tf) => (
              <button key={tf} onClick={() => setTimeframe(tf)} data-testid={`tf-${tf}`}
                className={clsx("px-2 py-1 rounded text-[11px] font-mono transition-colors", timeframe === tf ? "bg-brandblue text-white" : "text-fx-s3 hover:text-white hover:bg-white/5")}>
                {tf}
              </button>
            ))}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="indicators-btn" className="h-8 bg-fx-s1 border-white/10 text-xs gap-1"><SlidersHorizontal className="h-3.5 w-3.5" /> Indicators</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-fx-s2 border-white/10 text-white w-56">
              <DropdownMenuLabel>Overlays</DropdownMenuLabel>
              {OVERLAYS.map((k) => (
                <DropdownMenuCheckboxItem key={k} checked={overlays.includes(k)} onCheckedChange={() => toggleOverlay(k)} onSelect={(e) => e.preventDefault()} data-testid={`ind-${k}`} className="text-xs focus:bg-white/10">
                  {INDICATOR_META[k].label}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuLabel>Oscillator</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={oscillator || "none"} onValueChange={(v) => setOscillator(v === "none" ? null : v)}>
                <DropdownMenuRadioItem value="none" className="text-xs focus:bg-white/10">None</DropdownMenuRadioItem>
                {OSCILLATORS.map((k) => (
                  <DropdownMenuRadioItem key={k} value={k} onSelect={(e) => e.preventDefault()} data-testid={`osc-${k}`} className="text-xs focus:bg-white/10">{INDICATOR_META[k].label}</DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="ml-auto flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8 text-fx-s3" onClick={screenshot} data-testid="screenshot-btn"><Camera className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-fx-s3" onClick={goFullscreen} data-testid="fullscreen-btn"><Maximize2 className="h-4 w-4" /></Button>
          </div>
        </div>

        <div ref={chartWrapRef} className="flex-1 min-h-0 bg-fx-base overflow-hidden">
          <PriceChart candles={candles} chartType={chartType} overlays={overlays} oscillator={oscillator} height={oscillator ? 340 : 480} />
        </div>

        {/* Open trades */}
        <div className="border-t border-white/5 bg-fx-s1/40 max-h-40 overflow-y-auto">
          <div className="px-3 py-2 text-[11px] font-mono uppercase tracking-widest text-fx-s3 flex items-center gap-2"><Timer className="h-3.5 w-3.5" /> Open Positions ({openTrades.length})</div>
          {openTrades.length === 0 ? (
            <div className="px-3 pb-3 text-xs text-fx-s3">No open positions.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2 px-3 pb-3">
              {openTrades.map((o) => (
                <div key={o.id} data-testid={`open-trade-${o.id}`} className="rounded-lg bg-fx-base border border-white/5 p-2.5 flex items-center gap-3">
                  <div className={clsx("h-8 w-8 rounded grid place-items-center", ["buy","call"].includes(o.direction) ? "bg-pos/15 text-pos" : "bg-neg/15 text-neg")}>
                    {["buy","call"].includes(o.direction) ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-white">{o.symbol} · {usd(o.amount)}</div>
                    <div className={clsx("text-[11px] font-mono", o.winning ? "text-pos" : "text-neg")}>{o.winning ? "Winning" : "Losing"}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm text-white">{o.remaining}s</div>
                    <div className="text-[10px] text-fx-s3">left</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Trade panel (single instance; stacks below on mobile) */}
      <aside className="border-t lg:border-t-0 lg:border-l border-white/5 bg-fx-s1/40 overflow-y-auto" data-testid="trade-panel">
        <TradePanel symbol={symbol} price={livePrice} returnRate={asset?.return_rate ?? 0.85} onTraded={loadOpen} />
        <TerminalHeadlines />
      </aside>
    </div>
  );
}
